// effects/personal-details.effects.ts
import { inject } from '@angular/core'
import { createEffect } from '@ngrx/effects'
import { Actions, ofType } from '@ngrx/effects'
import { EducationService } from '../../services/education.service'
import { ToastrService } from 'ngx-toastr'
import { catchError, exhaustMap, map, mergeMap, of, switchMap } from 'rxjs'
import {
  createEducation,
  createEducationFailure,
  createEducationSuccess,
  deleteEducation,
  deleteEducationFailure,
  deleteEducationSuccess,
  loadEducations,
  loadEducationsFailure,
  loadEducationsSuccess,
  updateEducation,
  updateEducationFailure,
  updateEducationSuccess
} from './education.actions'
import { Store } from '@ngrx/store'
import { NgbModal } from '@ng-bootstrap/ng-bootstrap'

export const loadEducations$ = createEffect(
  (actions$ = inject(Actions), educationService = inject(EducationService)) =>
    actions$.pipe(
      ofType(loadEducations),
      switchMap(
        (
          action // Use action.filter here
        ) =>
          educationService.getEducations(action.params, action.page, action.limit).pipe(
            map(res =>
              loadEducationsSuccess({
                educations: res.educations
              })
            ),
            catchError(error => of(loadEducationsFailure({ error: error.message })))
          )
      )
    ),
  { functional: true }
)

export const createEducation$ = createEffect(
  (
    actions$ = inject(Actions),
    educationService = inject(EducationService),
    toastr = inject(ToastrService),
    store = inject(Store),
    modalService = inject(NgbModal)
  ) =>
    actions$.pipe(
      ofType(createEducation),
      exhaustMap(({ payload }) => {
        // 🔥 RETURN the inner observable here
        return educationService.createEducation(payload).pipe(
          map(created => {
            toastr.success('Education created successfully!', 'Success')
            // store.dispatch(loadEducations({ params: null, page: 1, limit: 10 }))
            modalService.dismissAll()
            return createEducationSuccess({ education: created })
          }),
          catchError(error => {
            toastr.error('There was an error creating the Education.', 'Error')
            return of(createEducationFailure({ error }))
          })
        )
      })
    ),
  { functional: true, dispatch: true }
)

export const updateEducation$ = createEffect(
  (
    actions$ = inject(Actions),
    educationService = inject(EducationService),
    toastr = inject(ToastrService),
    store = inject(Store),
    modalService = inject(NgbModal)
  ) =>
    actions$.pipe(
      ofType(updateEducation), // 🔁 Listen for updateEducation action
      exhaustMap(({ id, payload }) => {
        return educationService.updateEducation(id, payload).pipe(
          map(updated => {
            toastr.success('Education updated successfully!', 'Success')
            // store.dispatch(loadEducations({ params: null, page: 1, limit: 10 }))
            modalService.dismissAll()
            return updateEducationSuccess({ education: updated })
          }),
          catchError(error => {
            toastr.error('There was an error updating the Education.', 'Error')
            return of(updateEducationFailure({ error }))
          })
        )
      })
    ),
  { functional: true, dispatch: true }
)

export const deleteEducation$ = createEffect(
  (
    actions$ = inject(Actions),
    educationService = inject(EducationService),
    toastr = inject(ToastrService)
  ) =>
    actions$.pipe(
      ofType(deleteEducation),
      exhaustMap(action =>
        educationService.deleteEducation(action.id).pipe(
          map(() => {
            toastr.success('Education deleted successfully!')
            return deleteEducationSuccess({ id: action.id })
          }),
          catchError(error => {
            toastr.error('Failed to delete Education: ' + error.message)
            return of(deleteEducationFailure({ error }))
          })
        )
      )
    ),
  { functional: true }
)

export const educationEffects = {
  createEducation$,
  loadEducations$,
  updateEducation$,
  deleteEducation$
}
